import React from 'react';
import {
    Lightbulb,
    TrendDown,
    TrendUp,
    EnvelopeSimple,
    Article,
    Clock,
} from '@phosphor-icons/react';

/**
 * Strategy Corner — CEO Desk (Spec §6)
 *
 * Weekly destination. No charts, no graphs, no raw KPIs.
 * Strategic recommendations grounded in data, expressed as actionable advice.
 *
 * Card format: Observation → Diagnosis → Recommendation (§6.1)
 * MVP: Placeholder with example content. Recommendation engine populates
 * this once sufficient data accumulates (§6.2).
 */

// ============================================================
// EXAMPLE RECOMMENDATIONS — static content for MVP
// Replaced by recommendation engine once data accumulates.
// ============================================================
const EXAMPLE_RECOMMENDATIONS = [
    {
        id: 'rec-1',
        source: 'Email Outreach',
        sourceIcon: EnvelopeSimple,
        observation:
            'Email open rates dropped 15% this week compared to your monthly average.',
        diagnosis:
            'Subject lines in recent sends averaged 12 words — significantly longer than your best-performing templates. Historically, shorter question-based subjects perform 2x better for your audience.',
        recommendation:
            'Try these three subject line strategies based on your top-performing sends: (1) Lead with a question, (2) Keep under 7 words, (3) Reference a mutual connection or recent event.',
        age: 'Updated this week',
    },
    {
        id: 'rec-2',
        source: 'Article Agent',
        sourceIcon: Article,
        observation:
            'You rejected 7 of 12 recommended articles last week. Top reason: not relevant to fund thesis.',
        diagnosis:
            'The agent is surfacing articles from general tech publications that don\'t align with your sector focus. The relevance filter is currently set to "broad" rather than matching your stated investment thesis.',
        recommendation:
            'Update the Article Agent\'s relevance filter in Settings to narrow the scope to your core sectors: enterprise SaaS, developer tools, and AI infrastructure.',
        age: 'Updated this week',
    },
    {
        id: 'rec-3',
        source: 'Deal Sourcing',
        sourceIcon: TrendUp,
        observation:
            'Three portfolio companies are approaching their next funding milestone within 60 days.',
        diagnosis:
            'Based on runway data and growth metrics, these companies will likely begin fundraising conversations soon. Early positioning for follow-on investment typically yields better terms.',
        recommendation:
            'Schedule check-in calls with the founders of these three companies this month to discuss their plans and your appetite for follow-on participation.',
        age: 'Updated this week',
    },
];

// ============================================================
// RECOMMENDATION CARD — Observation / Diagnosis / Recommendation
// ============================================================
function RecommendationCard({ rec }) {
    const SourceIcon = rec.sourceIcon;

    return (
        <article className="bg-white rounded-2xl border border-slate-200 shadow-sm hover:shadow-md transition-all duration-150 ease-out overflow-hidden">
            {/* Card Header */}
            <div className="flex items-center justify-between px-6 pt-5 pb-3">
                <div className="flex items-center gap-2">
                    <SourceIcon weight="fill" className="w-4 h-4 text-brand-500" />
                    <span className="text-xs font-semibold text-slate-500 tracking-wide">
                        {rec.source}
                    </span>
                </div>
                <div className="flex items-center gap-1.5 text-xs text-slate-400">
                    <Clock weight="regular" className="w-3.5 h-3.5" />
                    {rec.age}
                </div>
            </div>

            {/* Card Body — Three-part format */}
            <div className="px-6 pb-6 space-y-4">
                {/* Observation */}
                <div>
                    <h4 className="text-xs font-bold text-slate-400 tracking-wider mb-1.5">
                        Observation
                    </h4>
                    <p className="text-base text-slate-900 font-medium leading-relaxed">
                        {rec.observation}
                    </p>
                </div>

                {/* Diagnosis */}
                <div className="pl-4 border-l-2 border-brand-200">
                    <h4 className="text-xs font-bold text-slate-400 tracking-wider mb-1.5">
                        Diagnosis
                    </h4>
                    <p className="text-sm text-slate-600 leading-relaxed">
                        {rec.diagnosis}
                    </p>
                </div>

                {/* Recommendation */}
                <div className="bg-brand-50 rounded-xl p-4">
                    <div className="flex items-start gap-2">
                        <Lightbulb weight="fill" className="w-5 h-5 text-brand-600 shrink-0 mt-0.5" />
                        <div>
                            <h4 className="text-xs font-bold text-brand-700 tracking-wider mb-1.5">
                                Recommendation
                            </h4>
                            <p className="text-sm text-brand-800 leading-relaxed">
                                {rec.recommendation}
                            </p>
                        </div>
                    </div>
                </div>
            </div>
        </article>
    );
}

// ============================================================
// STRATEGY CORNER PAGE
// ============================================================
export default function StrategyCorner({ embedded = false }) {
    return (
        <>
            {/* Page Header — hidden when embedded in CEO Office */}
            {!embedded && (
                <div className="mb-8">
                    <h1 className="text-[28px] font-extrabold text-slate-900 font-heading leading-tight">
                        Strategy Insights
                    </h1>
                    <p className="text-base text-slate-500 mt-1 max-w-2xl leading-relaxed">
                        Weekly recommendations based on your activity patterns. Each insight
                        connects what happened, why it matters, and what to do next.
                    </p>
                </div>
            )}

            {/* MVP Notice */}
            <div className="flex items-start gap-3 p-4 mb-8 bg-brand-50 border border-brand-100 rounded-xl">
                <Lightbulb weight="fill" className="w-5 h-5 text-brand-600 shrink-0 mt-0.5" />
                <div>
                    <p className="text-sm text-brand-800 font-medium">
                        These are example recommendations to show the format.
                    </p>
                    <p className="text-sm text-brand-600 mt-0.5">
                        Once your agents have processed enough decisions, real insights will
                        appear here — updated weekly to match your review rhythm.
                    </p>
                </div>
            </div>

            {/* Recommendation Cards */}
            <div className="space-y-5">
                {EXAMPLE_RECOMMENDATIONS.map((rec) => (
                    <RecommendationCard key={rec.id} rec={rec} />
                ))}
            </div>
        </>
    );
}
